import { Injectable } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { VideoRecorderModalComponent } from "src/shared/components/video-recorder-modal/video-recorder-modal.component";
const VIDEO_LENGTH_LIMIT: number = 60000;

@Injectable({
  providedIn: 'root'
})
export class HtmlVideoRecorder {

  videoModal: HTMLIonModalElement;
  videoBlob: Blob;
  isRecording: boolean = false;
  mimeType: string;
  recordingStartTime: number = 0;
  private recordingStopTime: number = 0;
  private stream: MediaStream;
  private videoElement: HTMLVideoElement;
  private mediaRecorder: any;
  private videoBlobs: Blob[];
  private videoUrl: string;
  private stopTimeout: any;

  constructor(
    private modalCtrl: ModalController
    ) {
  }

  async openVideoRecorderModal(): Promise<Blob> {
    return new Promise(async (resolve, reject) => {
      try {
        const modal = await this.modalCtrl.create({component: VideoRecorderModalComponent});
        this.videoModal = modal;
        modal.present();
        const { data } = await modal.onWillDismiss();
        resolve(data);
      } catch (error) {
        reject(error);
      }
    });
  }

  async convertVideoUrlToBlob(videoUrl: string): Promise<Blob> {
    try {
      const response = await fetch(videoUrl);
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error('Error converting video URL to blob:', error);
      throw error;
    }
  }

  async recordVideo(videoElement: HTMLVideoElement) {
    try {
      this.stream = await this.initVideoStream();
      videoElement.srcObject = this.stream;
      this.videoElement = videoElement;
      this.startVideoRecording(this.stream);
    } catch (error) {
      console.log('error on trying to record video:', error);
    }
  }

  initVideoStream(): Promise<MediaStream> {
    try {
      return navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 480 },
          height: { ideal: 640 },
          aspectRatio: { ideal: 0.75 },
          frameRate: 15,
          facingMode: 'user'
        }
      });
    } catch (error) {
      // If that fails, try to get any available camera
      return navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 480 },
          height: { ideal: 640 },
          aspectRatio: { ideal: 0.75 },
          frameRate: 15
        }
      });
    }
  }

  startVideoRecording(stream: MediaStream) {
    this.isRecording = true;
    this.recordingStartTime = Date.now();
    this.videoBlobs = [];
    let options: any;
    // Try VP9 first (better compression), fallback to VP8, then MP4
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp9')) {
      options = { mimeType: 'video/webm;codecs=vp9', videoBitsPerSecond: 500000 };
    } else if (MediaRecorder.isTypeSupported('video/webm')) {
      options = { mimeType: 'video/webm', videoBitsPerSecond: 500000 };
    } else {
      options = { mimeType: 'video/mp4', videoBitsPerSecond: 500000 };
    }
    this.mimeType = options.mimeType;
    this.mediaRecorder = new MediaRecorder(stream, options);
    this.mediaRecorder.start();
    this.onDataAvailable(this.mediaRecorder);
    this.onStop(this.mediaRecorder);

    this.stopTimeout = setTimeout(() => {
      this.stopVideoRecording();
    }, VIDEO_LENGTH_LIMIT);
  }

  onDataAvailable(mediaRecorder: MediaRecorder) {
    mediaRecorder.ondataavailable = (e: any) => {
            if (e.data && e.data.size > 0) {
                this.videoBlobs.push(e.data);
            }
        };
  }

  onStop(mediaRecorder: MediaRecorder) {
    mediaRecorder.onstop = (e: Event) => {
        const videoBuffer = new Blob(this.videoBlobs, {
            type: 'video/webm'
        });
        this.videoBlob = videoBuffer;
        this.videoUrl = window.URL.createObjectURL(videoBuffer);
        this.videoElement.srcObject = null;
        this.videoElement.src = this.videoUrl;
    };
  }

  getVideoBlob(): Blob {
    const videoBuffer = new Blob(this.videoBlobs, {
      type: this.mimeType,
    });
    return videoBuffer;
  }

  stopVideoRecording() {
    clearTimeout(this.stopTimeout);
    this.isRecording = false;
    this.recordingStopTime = Date.now();
    this.mediaRecorder?.stop();
    if (!this.videoBlob) {
      this.videoBlob = this.getVideoBlob();
    }
    this.stream.getVideoTracks().forEach((track: MediaStreamTrack) => track.stop());
  }

  get elapsedSeconds(): number {
    if (!this.recordingStartTime) {
      return 0;
    }
    const endTime = this.isRecording ? Date.now() : this.recordingStopTime;
    return Math.floor((endTime - this.recordingStartTime) / 1000);
  }
}
